export function createUid() {
  if (
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }
  if (
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.getRandomValues === "function"
  ) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0"));
    return [
      hex.slice(0, 4).join(""),
      hex.slice(4, 6).join(""),
      hex.slice(6, 8).join(""),
      hex.slice(8, 10).join(""),
      hex.slice(10, 16).join("")
    ].join("-");
  }
  return `uid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export const getDynamicRowValueKey = (settingId, index) =>
  `${settingId}@${index}`;

export const getDynamicRowFixedKey = (settingId, index) =>
  `${settingId}@${index}__fixed`;

export function parseBracketList(input = "", quantity) {
  if (Array.isArray(input)) {
    return input
      .slice(0, quantity)
      .map((value) =>
        Array.isArray(value)
          ? value.slice(0, 3).map((item) => parseFloat(item) || 0)
          : [0, 0, 0]
      );
  }
  const matches = (input.match(/\[.*?\]/g) || []).slice(0, quantity);
  return Array.from({ length: quantity }, (_, index) => {
    const match = matches[index];
    if (!match) return [0, 0, 0];
    return match
      .slice(1, -1)
      .split(",")
      .slice(0, 3)
      .map((value) => parseFloat(value) || 0);
  });
}

export function parseRepeatConfig(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  if (!value.startsWith("repeat:")) return null;
  const parts = value
    .slice("repeat:".length)
    .split(";")
    .map((segment) => segment.split("=").map((item) => item.trim()))
    .filter(([key, item]) => key && item);
  const map = Object.fromEntries(parts);
  return {
    source: map.source ? Number(map.source) : null,
    countId: map.count || null
  };
}

export function parseDynamicValueListConfig(raw) {
  if (!raw) return null;
  const [left, ...rest] = String(raw).trim().split("|");
  const match = left.trim().match(/^\(\*(\d+)(?:@(\d+))?\*\)$/);
  if (!match) return null;
  return {
    countId: match[1],
    countSection: match[2] ? Number(match[2]) : null,
    formula: rest.join("|").trim() || null
  };
}

export function parseValueListPairs(raw) {
  if (!raw) return [];
  const value = Array.isArray(raw) ? raw.join(",") : String(raw);
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((token) => {
      const separator = token.lastIndexOf(":");
      if (separator >= 0) {
        return {
          label: token.slice(0, separator).trim(),
          value: token.slice(separator + 1).trim()
        };
      }
      return { label: token, value: token };
    });
}

export function resolveText(text, cfg, dims, translate) {
  if (text == null) return "";
  let value = String(text).replace(
    /\(\*(\d+)(?:@(\d+))?\*\)/g,
    (_, id, section) => {
      const key = section ? `${id}@${section}` : id;
      return String(cfg[key] ?? "");
    }
  );
  value = value.replace(/\b(width|height|depth)\b/g, (_, key) =>
    String(cfg[key] ?? dims?.[key] ?? "")
  );
  if (/^[\d\s+\-*/().]+$/.test(value)) {
    try {
      const result = new Function(`return (${value});`)();
      if (Number.isFinite(Number(result))) return String(result);
    } catch {}
  }
  return translate(value);
}

export function parseRefMods(raw) {
  if (!raw) return { split: false, group: null, macroId: null };
  const value = String(raw).trim();
  const [expression, hint] = value.includes("|")
    ? [value.split("|")[0], value.split("|").slice(1).join("|")]
    : [value, null];
  const match = expression.match(/^\(\*(\d+)(?:@\d+)?\*\)$/);
  let split = false;
  let group = null;
  if (hint) {
    hint
      .split(/[;,]/)
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((token) => {
        const [keyValue, rawValue] = token
          .split(/[:=]/)
          .map((item) => item?.trim());
        const key = (keyValue || "").toLowerCase();
        const optionValue = rawValue == null ? "true" : rawValue;
        if (
          key === "split" ||
          key === "perinstance" ||
          (key === "ovr" && optionValue.toLowerCase() === "inst")
        )
          split = true;
        if (key === "group" || key === "group:")
          group = rawValue == null ? "" : optionValue;
      });
  }
  return { split, group, macroId: match ? match[1] : null };
}

export function parseSetting(value = "") {
  const result = {};
  String(value)
    .split(",")
    .forEach((part) => {
      const separator = part.indexOf(":");
      if (separator === -1) return;
      const key = part.slice(0, separator).trim();
      const rawValue = part.slice(separator + 1).trim();
      if (key) result[key] = rawValue;
    });
  ["width", "height", "depth"].forEach((dimension) => {
    if (result[dimension] != null)
      result[dimension] = Number(result[dimension]);
  });
  return result;
}

export const normalizeVec = (value) => {
  if (Array.isArray(value)) return JSON.stringify(value);
  if (typeof value === "string") return value;
  return "[0,0,0]";
};
